import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  ArrowUturnLeftIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useAuth } from '@/hooks/auth';
import {
  counterpartyOptionLabel,
  useCounterpartySearch,
} from '@/hooks/counterparty';
import { formatDecimalString } from '@/utils/decimal-display';
import { formatDate } from '@/utils/format';
import { useToastStore } from '@/stores/toast-store';
import { useManualAdjustments } from '../hooks/use-manual-adjustments';
import {
  useDeleteAdjustment,
  usePostAdjustment,
} from '../hooks/use-adjustment-mutations';
import { ManualAdjustmentFormModal } from './ManualAdjustmentFormModal';
import { trapTabWithin } from '../utils/focus-trap';
import type { CounterpartyOption, ManualAdjustment } from '@/types';

interface Props {
  onClose: () => void;
}

/** Ref callback that focuses the node when it mounts. */
const focusOnMount = (el: HTMLButtonElement | null) => el?.focus();

/** Square icon action in the row's Actions cell. The hit area is the button, not
 *  the glyph, and the focus ring is visible — these fire money operations. */
const iconActionCls =
  'rounded-md border border-transparent p-1.5 hover:border-lr-border hover:bg-lr-surface-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-500';

/** Confirm / cancel step of a two-step action: a real button, not a link, since
 *  the confirm is the click that moves cash. */
const confirmActionCls =
  'rounded-md px-2 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Icon action with its label shown on hover and on keyboard focus.
 *
 * Not the native `title`: it waits a second before appearing, and a DISABLED
 * button shows none at all — which is precisely the case that needs explaining
 * (Post before BO-8040 lands). The bubble opens to the LEFT because Actions is
 * the last column and the rows scroll: an above/below bubble is clipped by the
 * scrolling body on the first and last row.
 *
 * `aria-label` names the button, `aria-describedby` attaches the same hint for a
 * screen reader, so the icon is never the only carrier of meaning.
 */
const IconAction: React.FC<{
  id: string;
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ id, label, hint, onClick, disabled, className, children }) => (
  <span className="group relative inline-flex">
    <button
      type="button"
      // `aria-disabled`, not `disabled`: a disabled button takes no focus, so a
      // keyboard user could never surface the hint — and the one hint that
      // carries information the row does not otherwise show is exactly the
      // disabled case ("Back Office posting is not enabled yet"). The button
      // stays focusable and describable; the click is inert.
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled) onClick();
      }}
      aria-label={label}
      aria-describedby={id}
      className={clsx(
        iconActionCls,
        disabled && 'cursor-not-allowed opacity-40',
        className
      )}
    >
      {children}
    </button>
    <span
      id={id}
      role="tooltip"
      className="pointer-events-none absolute right-full top-1/2 z-20 mr-1 -translate-y-1/2 whitespace-nowrap rounded bg-lr-fg px-1.5 py-0.5 text-[11px] text-lr-surface opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
    >
      {hint}
    </span>
  </span>
);

const postErrorMessage = (status?: number): string => {
  switch (status) {
    case 503:
      return 'Posting to the Back Office is not enabled yet - contact operations.';
    case 409:
      return 'This adjustment was already posted.';
    case 502:
      // Not "rejected": a timeout is indistinguishable from a refusal here, and
      // the Back Office takes no idempotency key, so a blind retry can book the
      // same cash twice.
      return 'The Back Office did not confirm the posting - check it there before retrying.';
    case 403:
      return 'You do not have permission to post adjustments.';
    default:
      return 'Failed to post the adjustment.';
  }
};

/**
 * Manual adjustments for one counterparty (LR-563): the list plus create / edit
 * / delete / post-to-BO / reverse. Editable + deletable only while unposted;
 * after posting the only action is a reversal (сторно).
 *
 * The modal owns the counterparty choice, mirroring BillingPreviewModal /
 * GenerateStatementModal (LR-557 polish) — the header carries no orphan select.
 * The picker searches every counterparty, inactive ones marked: adjustments already
 * booked against a since-deactivated counterparty must stay readable. Booking a
 * NEW one is gated on the counterparty being active, the rule the serializer
 * enforces too (`validate_counterparty`; only an existing draft may keep its
 * counterparty).
 */
export const ManualAdjustmentsModal: React.FC<Props> = ({ onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const mouseDownOnBackdrop = useRef(false);
  const addToast = useToastStore((s) => s.addToast);
  // LR-680: booking, editing, deleting, posting and reversing an adjustment all
  // need `create_trades` (the backend's `CanCreateTrades` on the viewset). The
  // list itself is a read — a view-only user keeps it.
  const canTrade = useAuth().hasPermission('create_trades');

  // The picked row itself, not an id looked up in the loaded window: that window
  // moves with the search query, so the selection would blank out on the next
  // keystroke. `isActive` rides along for the booking gate below.
  const [selected, setSelected] = useState<CounterpartyOption | null>(null);
  const [cpQuery, setCpQuery] = useState('');
  // Snapshot the counterparty when the form opens: the picker above stays live,
  // and swapping it mid-form must not re-point a half-typed adjustment at
  // another counterparty. Edit/reverse carry their own row's counterparty.
  const [creatingFor, setCreatingFor] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [editing, setEditing] = useState<ManualAdjustment | null>(null);
  const [reversing, setReversing] = useState<ManualAdjustment | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmPostId, setConfirmPostId] = useState<number | null>(null);

  // LR-738: searched and paged server-side, as the other billing pickers are
  // (LR-572). One 1000-row window filtered in the browser left every
  // counterparty past it unreachable on a large registry.
  // Deliberately the whole registry, NOT BillingPreviewModal's active slice, for
  // the read-reachability reason above: a deactivated counterparty's booked
  // adjustments must stay listable.
  const {
    data: cpPages,
    options,
    isLoading: cpLoading,
    isError: cpError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCounterpartySearch(cpQuery, false);

  const selectedValue = selected ? String(selected.id) : '';
  const selectedLabel = selected
    ? counterpartyOptionLabel(selected.name, selected.isActive)
    : undefined;
  // The picked option is always in the loaded pages: `options` is built from them.
  // The option SearchableSelect passes along is not used: it carries only
  // value/label, and the pick needs `isActive` and the bare name. Were the row
  // ever missing, the current pick stays rather than silently clearing the list.
  // Picking the current row again keeps it: its entry is labelled from the pick
  // (below), so taking the window's copy would do other than the entry says.
  const pickCounterparty = (value: string) => {
    if (value === selectedValue) return;
    const row = cpPages?.pages
      .flatMap((page) => page.results)
      .find((cp) => String(cp.id) === value);
    if (row) setSelected(row);
  };
  // One source for the pick: the booking gate reads the row as it was picked, and
  // so does the trigger. SearchableSelect labels it from a loaded option first,
  // and a later window may carry a different copy of the row, so that option is
  // relabelled here: the name never contradicts the button next to it.
  // NOTE: the pick is the row as the answer it came from had it. A registry change
  // made since shows up only when the row is picked anew (after another one)
  // from an answer fetched after that change, and within the hook's `staleTime`
  // a cached answer may predate it. The serializer refuses an inactive
  // counterparty (`validate_counterparty`), so the ceiling is a 400 with its
  // message, not a wrong booking. Upgrade path: gate on the pick fetched by id
  // (`useCounterpartyDetail`, as BillingPage resolves its filter).
  const pickerOptions = useMemo(
    () =>
      selectedLabel === undefined
        ? options
        : options.map((o) =>
            o.value === selectedValue ? { ...o, label: selectedLabel } : o
          ),
    [options, selectedValue, selectedLabel]
  );

  // Explicit limit: the registry default is one 100-row page, and an audit
  // surface that silently drops a counterparty's older corrections is worse than
  // a long list. `count` is rendered below so any real truncation is visible.
  const { data, isLoading, isFetching, isPlaceholderData, error } =
    useManualAdjustments(
      selected ? { counterparty: selected.id, limit: 500 } : null
    );
  const rows = useMemo(() => data?.results ?? [], [data]);
  const deleteMut = useDeleteAdjustment();
  const postMut = usePostAdjustment();

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  // A pending confirm belongs to one row of one counterparty; a switch or a
  // refetch that changes the rows underneath must not leave it armed against
  // whatever now sits in that position.
  useEffect(() => {
    setConfirmDeleteId(null);
    setConfirmPostId(null);
  }, [selectedValue, rows]);

  const handlePost = (adj: ManualAdjustment) => {
    postMut.mutate(adj.id, {
      onSuccess: () => {
        setConfirmPostId(null);
        // The focused Confirm button unmounts with this branch; without this,
        // focus falls to <body>, where the dialog's Escape handler and Tab trap
        // never see a keydown and the user is silently ejected from the modal.
        dialogRef.current?.focus();
        addToast('success', 'Adjustment posted to the Back Office.');
      },
      onError: (err) => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        addToast('error', postErrorMessage(status));
      },
    });
  };

  const handleDelete = (adj: ManualAdjustment) => {
    deleteMut.mutate(adj.id, {
      onSuccess: () => {
        setConfirmDeleteId(null);
        dialogRef.current?.focus();
        addToast('success', 'Adjustment deleted.');
      },
      onError: (err) => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        addToast(
          'error',
          status === 409
            ? 'This adjustment was already posted and cannot be deleted.'
            : status === 403
              ? // Same gate as Post; say the same thing rather than a generic
                // failure the user cannot act on.
                'You do not have permission to delete adjustments.'
              : 'Failed to delete the adjustment.'
        );
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (mouseDownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Manual adjustments"
        tabIndex={-1}
        // No overflow-hidden on the shell: SearchableSelect drops its list as an
        // absolute child and a clipping dialog would cut it at the edge (same
        // reason as BillingPreviewModal). The scrolling body clips itself.
        // Same width as BillingPreviewModal: six columns, one of them the free-text
        // comment that wraps rather than truncates, so 3xl squeezed the money
        // columns to make room for it.
        className="flex max-h-[80vh] w-full max-w-5xl flex-col rounded-[10px] bg-lr-surface shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          trapTabWithin(dialogRef.current, e);
        }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-lr-border px-6 py-4">
          <div className="shrink-0 text-[15px] font-semibold text-lr-fg">
            Manual adjustments
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <SearchableSelect
              value={selectedValue}
              onChange={pickCounterparty}
              options={pickerOptions}
              selectedLabel={selectedLabel}
              placeholder="Select counterparty"
              searchPlaceholder="Search counterparties…"
              // Not disabled while loading, as in BillingPreviewModal: the
              // dropdown says "Loading…" itself, and locking the control would
              // block typing into the search that resolves it.
              className="w-64"
              onSearchChange={setCpQuery}
              isLoading={cpLoading}
              isError={cpError}
              hasMore={hasNextPage}
              // `cancelRefetch: false` so a burst of scroll events joins the page
              // already in flight instead of cancelling and re-issuing it.
              onLoadMore={() => fetchNextPage({ cancelRefetch: false })}
              isLoadingMore={isFetchingNextPage}
              buttonProps={{
                'aria-label': 'Adjustments counterparty',
                'data-testid': 'adjustments-cp',
              }}
            />
            {canTrade && (
              <button
                type="button"
                // Booking needs a live counterparty; reading a deactivated one's
                // history does not.
                disabled={!selected?.isActive}
                onClick={() =>
                  selected &&
                  setCreatingFor({ id: selected.id, name: selected.name })
                }
                className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                New adjustment
              </button>
            )}
          </div>
        </div>

        {/* `keepPreviousData` suppresses the loading state on a counterparty
            switch, so without this the header names one counterparty while the
            rows below still belong to another — with live Post/Delete buttons on
            them. pointer-events-none is the load-bearing half: stale money
            actions must be unclickable, not merely faint. `inert` does the same
            for the keyboard, which pointer-events never stopped. */}
        <div
          className={clsx(
            'flex-1 overflow-auto',
            isFetching && 'pointer-events-none opacity-50'
          )}
          aria-busy={isFetching}
          // Only while the rows are the previous counterparty's. A refetch of the
          // same one is what a post or delete awaits (its `onSuccess` returns the
          // invalidation), with focus still on the row's Confirm, and inert would
          // drop it to <body>. A spread: React 18 has no `inert` prop.
          {...(isPlaceholderData ? { inert: '' } : {})}
        >
          {!selected ? (
            <div className="py-12 text-center text-sm text-lr-muted">
              Select a counterparty to see its manual adjustments.
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-lr-neg">
              Failed to load adjustments.
            </div>
          ) : isLoading ? (
            <div className="py-12 text-center text-sm text-lr-muted">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-lr-muted">
              No adjustments for this counterparty yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-lr-surface-2 text-xs text-lr-muted">
                <tr className="border-b border-lr-border">
                  <th className="px-4 py-2 text-left font-medium">
                    Instrument
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    Trade type
                  </th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="px-4 py-2 text-left font-medium">
                    Value date
                  </th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  {canTrade && (
                    <th className="px-4 py-2 text-right font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((adj) => {
                  // Read the decomposed direction the server sends rather than
                  // re-parsing the sign off the Decimal string — one source for
                  // the convention, and no string method on a money field.
                  const isReceivable = adj.direction === 'to_us';
                  const confirming = confirmDeleteId === adj.id;
                  return (
                    <tr
                      key={adj.id}
                      className="border-b border-lr-border-soft align-top"
                    >
                      <td className="px-4 py-2 text-lr-fg">
                        {adj.symbol_id}
                        {adj.reverses !== null && (
                          <span className="ml-1 text-xs text-lr-muted">
                            (reversal)
                          </span>
                        )}
                        {/* The reason is rendered, not hung off a `title`: a
                            tooltip on a non-focusable cell reaches neither the
                            keyboard nor a screen reader, and on a money
                            correction the comment IS the record. Wraps rather
                            than truncates — free text has no width. */}
                        <div className="mt-0.5 break-words text-xs text-lr-muted">
                          {adj.comment}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-lr-fg-2">
                        {adj.trade_type_display}
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-2 text-right font-semibold tabular-nums',
                          isReceivable ? 'text-lr-pos' : 'text-lr-neg'
                        )}
                      >
                        {formatDecimalString(adj.amount, {
                          currency: adj.currency,
                        })}
                        <div className="whitespace-nowrap text-xs font-normal text-lr-muted">
                          {isReceivable ? 'to us' : 'to counterparty'}
                        </div>
                      </td>
                      {/* An ISO date is one token; letting the column steal width
                          from the free-text comment beats breaking `2026-08-07`
                          across two lines. */}
                      <td className="whitespace-nowrap px-4 py-2 text-lr-fg-2">
                        {adj.value_date}
                      </td>
                      {/* Two tokens since LR-680 (pill + `Reversed`) — same
                          reason the date and the direction caption next to it
                          are nowrap: the column should steal width from the
                          free-text comment rather than stack. */}
                      <td className="whitespace-nowrap px-4 py-2">
                        <span
                          className={clsx(
                            'rounded-full px-2 py-0.5 text-xs font-medium',
                            adj.is_posted
                              ? 'bg-lr-bg text-lr-muted'
                              : 'bg-lr-warn-weak text-lr-warn'
                          )}
                        >
                          {adj.is_posted ? 'Posted' : 'Draft'}
                        </span>
                        {/* `is_reversed` is STATE, not a control, and it used to
                            be readable only out of the Actions slot — which now
                            drops for a view-only user (LR-680). On a money ledger
                            that is the difference between a live correction and
                            one already undone, and the backend serves the flag on
                            an open GET, so it belongs in the Status cell. */}
                        {adj.is_reversed && (
                          <span className="ml-1 text-xs text-lr-muted">
                            Reversed
                          </span>
                        )}
                      </td>
                      {/* LR-680: the adjustments list is a read and stays open to
                          everyone; its write actions need `create_trades`. Header
                          and cell drop together, or the columns misalign. */}
                      {canTrade && (
                        <td className="px-4 py-2 text-right">
                          {/* Both confirm steps swap the focused button out, so the
                              replacement announces itself and takes focus — without
                              that, a keyboard user is dropped to <body> and a
                              screen reader hears nothing at all. */}
                          {confirming ? (
                            <span
                              className="inline-flex items-center justify-end gap-1"
                              role="alert"
                            >
                              <button
                                type="button"
                                ref={focusOnMount}
                                onClick={() => handleDelete(adj)}
                                disabled={deleteMut.isPending}
                                className={clsx(
                                  confirmActionCls,
                                  'bg-lr-neg text-white hover:opacity-90'
                                )}
                              >
                                Confirm delete
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                className={clsx(
                                  confirmActionCls,
                                  'border border-lr-border text-lr-fg-2 hover:bg-lr-surface-2'
                                )}
                              >
                                Cancel
                              </button>
                            </span>
                          ) : confirmPostId === adj.id ? (
                            <span
                              className="inline-flex items-center justify-end gap-1"
                              role="alert"
                            >
                              <button
                                type="button"
                                ref={focusOnMount}
                                onClick={() => handlePost(adj)}
                                disabled={postMut.isPending}
                                className={clsx(
                                  confirmActionCls,
                                  'bg-lr-pos text-white hover:opacity-90'
                                )}
                              >
                                Confirm post{' '}
                                {formatDecimalString(adj.amount, {
                                  currency: adj.currency,
                                })}{' '}
                                on {formatDate(adj.value_date)}
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmPostId(null)}
                                className={clsx(
                                  confirmActionCls,
                                  'border border-lr-border text-lr-fg-2 hover:bg-lr-surface-2'
                                )}
                              >
                                Cancel
                              </button>
                            </span>
                          ) : adj.is_editable ? (
                            <span className="inline-flex justify-end gap-1">
                              <IconAction
                                id={`adj-edit-${adj.id}`}
                                label="Edit"
                                hint="Edit adjustment"
                                onClick={() => setEditing(adj)}
                                className="text-primary-600"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </IconAction>
                              <IconAction
                                id={`adj-delete-${adj.id}`}
                                label="Delete"
                                hint="Delete adjustment"
                                onClick={() => setConfirmDeleteId(adj.id)}
                                className="text-lr-neg"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </IconAction>
                              {/* Post books real cash and locks the row for good,
                                  so it asks before acting — the cheaper, reversible
                                  Delete already did. */}
                              <IconAction
                                id={`adj-post-${adj.id}`}
                                label="Post"
                                hint={
                                  adj.can_post
                                    ? 'Post to the Back Office'
                                    : 'Back Office posting is not enabled yet'
                                }
                                onClick={() => setConfirmPostId(adj.id)}
                                disabled={!adj.can_post}
                                className="text-lr-pos"
                              >
                                <PaperAirplaneIcon className="h-4 w-4" />
                              </IconAction>
                            </span>
                          ) : adj.is_reversed || adj.reverses !== null ? (
                            // Nothing left to offer, for two separate reasons that
                            // share one empty slot. `is_reversed`: one сторно per
                            // adjustment — the backend refuses a second, so a button
                            // here is a guaranteed 400 (the row says "Reversed" in
                            // its Status cell). `reverses !== null`: a сторно of a
                            // сторно. The requirement is only that a posted
                            // adjustment is corrected by a counter-adjustment, and it
                            // says nothing about chaining — so the form does not
                            // invite O ← R ← R2, whose net effect is the original
                            // booked twice under two different-looking rows. Refusing
                            // it outright would be inventing a rule product never
                            // asked for: the API still accepts one, deliberately.
                            <span className="text-xs text-lr-muted">—</span>
                          ) : (
                            <span className="inline-flex justify-end">
                              <IconAction
                                id={`adj-reverse-${adj.id}`}
                                label="Reverse"
                                hint="Reverse with a counter-adjustment"
                                onClick={() => setReversing(adj)}
                                className="text-primary-600"
                              >
                                <ArrowUturnLeftIcon className="h-4 w-4" />
                              </IconAction>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {data && data.count > rows.length && (
            <div className="px-4 py-2 text-xs text-lr-muted">
              Showing the {rows.length} most recent of {data.count}.
            </div>
          )}
        </div>

        <div className="flex justify-end rounded-b-[10px] border-t border-lr-border px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-lr-border bg-lr-surface px-4 py-2 text-sm font-medium text-lr-fg-2 hover:bg-lr-surface-2"
          >
            Close
          </button>
        </div>
      </div>

      {creatingFor && (
        <ManualAdjustmentFormModal
          counterparty={creatingFor}
          onClose={() => setCreatingFor(null)}
        />
      )}
      {/* Edit / reverse take the counterparty off the row itself, so they stay
          pinned to it even if the picker above moves. */}
      {editing && (
        <ManualAdjustmentFormModal
          counterparty={{
            id: editing.counterparty,
            name: editing.counterparty_name,
          }}
          adjustment={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {reversing && (
        <ManualAdjustmentFormModal
          counterparty={{
            id: reversing.counterparty,
            name: reversing.counterparty_name,
          }}
          reverseOf={reversing}
          onClose={() => setReversing(null)}
        />
      )}
    </div>
  );
};
